import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, Bookmark, Trash2, LayoutTemplate } from "lucide-react";
import { CADENCE_STYLES } from "./campaignModel";

const optionClass = (active) =>
  `p-3 rounded-lg cursor-pointer border transition-colors ${active ? "border-ism-teal/50 bg-ism-teal/10" : "border-white/5 bg-white/5 hover:border-white/10"}`;

export const LeadPicker = ({ leads, loading, selectedIds, onToggle, onToggleAll }) => {
  if (loading) {
    return <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-ism-teal" /></div>;
  }
  if (leads.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">No leads in CRM yet</p>;
  }
  const allSelected = selectedIds.length === leads.length;
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
        <Button variant="ghost" size="sm" className="text-xs text-ism-teal" onClick={onToggleAll} data-testid="toggle-all-leads-btn">
          {allSelected ? "Deselect All" : "Select All"}
        </Button>
      </div>
      {leads.map((lead) => {
        const on = selectedIds.includes(lead.leadId);
        return (
          <div
            key={lead.leadId}
            onClick={() => onToggle(lead.leadId)}
            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${on ? "bg-ism-teal/10 border border-ism-teal/30" : "bg-white/5 border border-white/5 hover:border-white/10"}`}
            data-testid={`lead-select-${lead.leadId}`}
          >
            <div className={`w-5 h-5 rounded border flex items-center justify-center ${on ? "bg-ism-teal border-ism-teal" : "border-white/20"}`}>
              {on && <Check className="h-3 w-3 text-black" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{lead.contactName || lead.companyName}</p>
              <p className="text-xs text-gray-500 truncate">{lead.contactTitle ? `${lead.contactTitle} at ` : ""}{lead.companyName}</p>
            </div>
            {lead.contactEmail && <span className="text-xs text-gray-600 font-mono">{lead.contactEmail}</span>}
          </div>
        );
      })}
    </div>
  );
};

export const TemplatePicker = ({ templates, selectedId, onSelect, onDelete }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    <div onClick={() => onSelect(null)} className={optionClass(!selectedId)} data-testid="template-option-ai">
      <div className="flex items-center gap-2">
        <Sparkles className={`h-4 w-4 ${!selectedId ? "text-ism-teal" : "text-gray-500"}`} />
        <p className={`text-sm font-medium ${!selectedId ? "text-ism-teal" : "text-white"}`}>AI-Determined</p>
      </div>
      <p className="text-xs text-gray-500 mt-1">Let ISM design the optimal cadence</p>
    </div>
    {templates.map((tpl) => {
      const active = selectedId === tpl.templateId;
      const cfg = tpl.cadenceConfig || {};
      return (
        <div key={tpl.templateId} onClick={() => onSelect(tpl)} className={`${optionClass(active)} relative group`} data-testid={`template-option-${tpl.templateId}`}>
          <div className="flex items-center gap-2 pr-6">
            <Bookmark className={`h-4 w-4 flex-shrink-0 ${active ? "text-ism-teal" : "text-gray-500"}`} />
            <p className={`text-sm font-medium truncate ${active ? "text-ism-teal" : "text-white"}`}>{tpl.name}</p>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {cfg.steps || (cfg.stepLabels || []).length} steps · {tpl.cadenceStyle || cfg.style || "consultative"}
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(tpl.templateId); }}
            className="absolute top-2 right-2 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`delete-template-${tpl.templateId}`}
            title="Remove template"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      );
    })}
  </div>
);

export const StylePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {CADENCE_STYLES.map((s) => (
      <div key={s.value} onClick={() => onChange(s.value)} className={optionClass(value === s.value)} data-testid={`style-${s.value}`}>
        <p className={`text-sm font-medium ${value === s.value ? "text-ism-teal" : "text-white"}`}>{s.label}</p>
        <p className="text-xs text-gray-500">{s.desc}</p>
      </div>
    ))}
  </div>
);

export const CadenceNote = ({ template }) => {
  const Icon = template ? LayoutTemplate : Sparkles;
  return (
    <div className="glass-card p-4 border-l-4 border-ism-teal/50" data-testid={template ? "template-note" : "ai-cadence-note"}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 text-ism-teal flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-white">{template ? `Using template: ${template.name}` : "AI-Determined Cadence"}</p>
          <p className="text-xs text-gray-500 mt-1">
            {template
              ? "The saved cadence structure (steps & timing) will be reused. The AI still writes each email personalized to every prospect."
              : "The number of steps, timing, and approach will be intelligently determined based on your market, product complexity, average deal size, and the seniority of your prospects."}
          </p>
        </div>
      </div>
    </div>
  );
};
